import React from 'react';
import { ExternalLink } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';
import css from '../TermsOfServicePage.module.css';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

function ProfessionalTosEnglish() {
  const history = useHistory();
  return (
    <div className="terms-page">
      <h1>Terms of Use for Professionals & Experts</h1>

      <h2>DOCUMENT INFORMATION</h2>
      <p>
        Applies to: Professional Users (Providers) offering services via the VivianLab Platform
        <br />
        Last amended: 5/12/2025
        <br />
        Related documents:{' '}
        <span
          className={css.termsLink}
          role="button"
          onClick={() => history.push('/terms-of-service?tab=consumer')}
        >
          Terms of Use for Consumers
        </span>{' '}
        | <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink> |{' '}
        <ExternalLink href="/data-processing-addendum">
          Data Processing Agreement (Appendix 1)
        </ExternalLink>
      </p>

      <h2>Preamble</h2>
      <p>
        These Terms of Use apply exclusively to Professionals & Experts (also referred to as
        "Providers") who register and offer services through the website located at
        www.vivianlab.gr, www.vivianlab.com, and www.vivianlab.health, and related mobile
        applications (collectively, the "Platform").
      </p>
      <p>
        The Platform is owned and operated by the company under the name “VIVIANLAB LTD" and the
        distinctive title “Vivian Lab", with registered office at 1612 SOFIA BUL. TSAR BORIS III № 81
        ET.1, AP.1, VAT BG207933919, electronic contact info@vivianlab.com (the “Company”).
      </p>
      <p>
        All content, trademarks, and domain names (including www.vivianlab.gr, www.vivianlab.com,
        and www.vivianlab.health) are the exclusive property of the Company and are protected by
        copyright, trademark, and other applicable intellectual property rights.
      </p>
      <p>
        The Company is committed to ensuring that the Platform is accessible to all users, including
        those with disabilities, in compliance with the European Accessibility Act (Directive (EU)
        2019/882) and relevant technical standards.
      </p>

      <h2>1. DEFINITIONS</h2>
      <p>For purposes of these Terms of Use:</p>
      <ul>
        <li>
          <strong>“Professional User” or “Provider”:</strong> Any individual or legal entity
          registered on the Platform to offer professional services (including health specialists,
          consultants, and businesses active in the health and wellness sector).
        </li>
        <li>
          <strong>"Consumer" or "User":</strong> Any individual using the Platform to seek, book, or
          receive services or digital content from Professional Users.
        </li>
        <li>
          <strong>"Platform" or "Marketplace":</strong> The online environment operated by the
          Company under the brand name VivianLab, accessible via website and mobile applications.
        </li>
        <li>
          <strong>“Company":</strong> “VIVIANLAB LTD" with registered office at 1612 SOFIA BUL. TSAR
          BORIS III № 81 ET.1, AP.1, VAT BG207933919, electronic contact info@vivianlab.com, the
          legal entity that owns and operates the Platform.
        </li>
        <li>
          <strong>“Services”:</strong> All intermediary services provided by the Company through the
          Platform, including profile display, appointment booking, video consultations, educational
          content, and AI-assisted tools.
        </li>
        <li>
          <strong>“Agreement”:</strong> These Terms of Use, together with the{' '}
          <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink> and{' '}
          <ExternalLink href="/data-processing-addendum">Data Processing Agreement</ExternalLink> .
        </li>
        <li>
          <strong>“Business Days”:</strong> Any day other than Saturday, Sunday, or public holiday
          in Greece.
        </li>
        <li>
          <strong>“Session” or “Appointment”:</strong> A scheduled consultation or service delivery
          between a Professional User and a Consumer via the Platform.
        </li>
        <li>
          <strong>“Professional Content”:</strong> Any information, advice, recommendations,
          educational material, or other content (including during webinars, sessions, articles,
          posts, and videos) provided by the Professional User through the Platform or in
          collaboration with the Company.
        </li>
        <li>
          <strong>“Co-Created Content”:</strong> Any content (including, but not limited to, videos,
          webinars, online courses, articles, social media content, interviews, and recordings) that
          is created jointly by the Professional User and the Company or produced by the Company
          featuring the Professional User.
        </li>
      </ul>

      <h2>2. ACCEPTANCE OF TERMS</h2>
      <p>
        By registering on or using the Platform, you (the Professional User) fully and
        unconditionally accept and agree to be bound by:
      </p>
      <ol>
        <li>These Terms of Use for Professional Users;</li>
        <li>
          The <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>;
        </li>
        <li>
          <ExternalLink href="/data-processing-addendum">
            Data Processing Agreement (Appendix 1)
          </ExternalLink>
          ; and
        </li>
        <li>
          <ExternalLink href="/cookie-policy">The Cookie Policy</ExternalLink>.
        </li>
      </ol>
      <p>If you do not accept these terms, you must not register or use the Platform.</p>

      <h2>3. DATA PROTECTION AND GDPR ROLES</h2>
      <h3>3.1 The Company as Data Controller</h3>
      <p>
        The Company acts as Data Controller for personal data you provide directly to the Company
        for purposes such as:
      </p>
      <ul>
        <li>Creating and managing your Professional User account</li>
        <li>Processing subscription payments</li>
        <li>Sending newsletters and marketing communications (with your consent)</li>
        <li>Platform administration and security</li>
      </ul>
      <p>
        All information regarding purposes, legal bases for processing, retention periods, data
        recipients, and your rights as a data subject is detailed in our{' '}
        <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>.
      </p>
      <h3>3.2 The Company as Data Processor</h3>
      <p>
        When you provide services to Consumers via the Platform, you (the Professional User) act as
        the Data Controller for personal data related to those services (including appointment data,
        consultation notes, and any health data).
      </p>
      <p>
        In this situation, the Company acts as the Data Processor, collecting and processing this
        data solely on your behalf and upon your explicit instructions.
      </p>
      <p>
        The legal relationship, security measures, and data processing obligations are governed by
        the{' '}
        <ExternalLink href="/data-processing-addendum">
          Data Processing Agreement (DPA)
        </ExternalLink>
        , which is attached as Appendix 1 and forms an integral part of these Terms of Use.
      </p>
      <p>
        By accepting these Terms of Use, you simultaneously accept the{' '}
        <ExternalLink href="/data-processing-addendum">
          Data Processing Agreement (Appendix 1)
        </ExternalLink>
        .
      </p>

      <h3>
        <FormattedMessage id="ProfileImagesSection.titleWithNumber" values={{ number: '3.3' }} />
      </h3>
      <ul>
        <li>
          <FormattedMessage id="ProfileImagesSection.cameraAccess" />
        </li>
        <li>
          <FormattedMessage id="ProfileImagesSection.usage" />
        </li>
        <li>
          <FormattedMessage id="ProfileImagesSection.storage" />
        </li>
      </ul>

      <h2>4. ONLINE SERVICES PROVIDED BY THE PLATFORM</h2>
      <p>
        The Platform is an intermediary marketplace specialising in health, wellness, and related
        consulting services. The Platform does not itself provide medical, health, or professional
        services; it provides only the technical means for Professional Users to connect with and
        deliver services to Consumers.
      </p>
      <p>Services available through the Platform include:</p>
      <ul>
        <li>
          Professional profile display (contact details, qualifications, specialisations, services
          offered, pricing, working hours)
        </li>
        <li>Online appointment booking</li>
        <li>Video consultations (via Meet, Calendly, or the Platform’s own system)</li>
        <li>Group webinars and hotlines</li>
        <li>Chat and messaging services</li>
        <li>Educational content (articles, videos, recipes)</li>
        <li>AI-assisted tools (symptom tracking, personalised guidance)</li>
      </ul>
      <p>
        The Company undertakes to ensure that the Platform and the means for providing online
        services conform to the contract and are fit for the purposes for which digital intermediary
        services would normally be used, with the professional diligence and skill expected.
      </p>

      <h2>5. PROFESSIONAL USER OBLIGATIONS</h2>
      <h3>5.1 Registration and Profile Requirements</h3>
      <p>Professional Users must:</p>
      <ul>
        <li>
          Provide accurate, complete, and up-to-date information, including:
          <ul>
            <li>Full name (or legal entity name)</li>
            <li>Professional address and headquarters</li>
            <li>Contact information (phone, email, website)</li>
            <li>
              Practice licence number (as issued by the relevant professional association or
              regulatory body)
            </li>
            <li>Professional qualifications, certifications, and areas of specialization</li>
          </ul>
        </li>
        <li>
          Update their profile promptly when information changes, including license renewals or
          expirations
        </li>
      </ul>
      <p>
        The Professional User is solely responsible for submitting true and accurate information and
        guarantees both its legality and lawful possession and use. The Professional User is liable
        for any damages the Company may suffer due to violation of this obligation.
      </p>
      <h3>5.2 Compliance with Laws and Professional Standards</h3>
      <p>Professional Users must:</p>
      <ul>
        <li>
          Comply with all applicable national, European, and international laws, including but not
          limited to:
          <ul>
            <li>Consumer protection laws (Law 2251/1994, PD 131/2003)</li>
            <li>E-commerce and electronic communications regulations</li>
            <li>Data protection legislation (GDPR)</li>
            <li>Professional codes of ethics and conduct</li>
          </ul>
        </li>
        <li>Not engage in misleading advertising or unfair competition</li>
        <li>Ensure pricing complies with economic transparency requirements</li>
        <li>Maintain necessary insurance coverage for the services provided</li>
        <li>
          Issue proper tax documents (receipts, invoices) to Consumers and the Company as required
          by law
        </li>
      </ul>
      <p>
        The Company does not set, control, or verify service pricing. Professional Users are solely
        responsible for setting fees and ensuring compliance with all applicable pricing and
        transparency laws.
      </p>
      <h3>5.3 Service Delivery and Professional Conduct</h3>
      <p>Professional Users must:</p>
      <ul>
        <li>Respond promptly to communications from the Company and Consumers</li>
        <li>Deliver services with professionalism and diligence</li>
        <li>Maintain confidentiality of all information received from Consumers</li>
        <li>
          Not misuse Consumer data for any purpose other than delivering the contracted service
        </li>
        <li>Act in good faith and with business ethics</li>
      </ul>
      <p>Professional advice and information</p>
      <p>Professional Users acknowledge and agree that:</p>
      <ul>
        <li>
          They are solely responsible for all advice, information, and content they provide to
          Consumers and to the public through the Platform or in any collaboration with the Company.
        </li>
        <li>
          They shall ensure that all advice and information is accurate, up to date, and compliant
          with applicable laws, professional standards, and ethical codes.
        </li>
        <li>
          They shall not provide medical advice or diagnosis if they are not duly licensed and
          authorised to do so under applicable law.
        </li>
        <li>
          They remain fully and exclusively responsible for any consequences arising from their
          advice, recommendations, or omissions.
        </li>
      </ul>
      <p>
        If a Professional User fails to provide the agreed service within the specified timeframe
        without prior written notice and valid reason, the Company may, at its discretion, suspend
        or terminate the Professional User’s access, withhold or refund payments, or take other
        remedies as necessary. Repeated or unjustified failures may result in permanent removal from
        the Platform and/or legal action.
      </p>
      <h3>5.4 Prohibition on Unauthorised Appointments and Cancellations</h3>
      <p>
        If a Professional User cancels four (4) or more appointments scheduled through the Platform
        without valid reason, the Company reserves the right to terminate the cooperation and remove
        the Professional User's profile from the Platform.
      </p>
      <h3>5.5 Account Security</h3>
      <p>
        Professional Users are responsible for maintaining the confidentiality and security of their
        account credentials, including passwords and authentication information. Professional Users
        must choose strong, unique passwords and take reasonable steps to protect their accounts
        from unauthorised access. The Company is not liable for any loss or damage resulting from a
        Professional User’s failure to comply with these account security obligations.
      </p>

      <h2>6. PRICING, PAYMENTS, AND FINANCIAL TERMS</h2>
      <h3>6.1 Service Pricing</h3>
      <ul>
        <li>Professional Users set their own service prices.</li>
        <li>The Company does not intervene in pricing decisions.</li>
        <li>
          Professional Users are solely responsible for:
          <ul>
            <li>Ensuring pricing complies with all applicable economic and transparency laws</li>
            <li>Issuing proper tax documentation (invoices, receipts) to Consumers</li>
          </ul>
        </li>
      </ul>
      <p>
        Professional Users may offer group sessions or hotlines for free or with payment facilitated
        by the Company. Professional Users offering free group sessions or hotlines acknowledge and
        agree that they are solely responsible for the content and delivery of such sessions and
        will not expect or claim any reimbursement, payment, or compensation from the Company under
        any circumstances, including cases where no Consumers participate.
      </p>
      <h3>6.2 Payment Processing</h3>
      <p>
        Payments are processed through Stripe, a third-party payment processor. By registering
        payment details, Professional Users accept Stripe’s Terms and Conditions.
      </p>
      <p>
        The Company does not directly handle payments and is not responsible for payment processing
        issues.
      </p>
      <p>Payment process:</p>
      <ol>
        <li>
          When a Consumer books an appointment, their credit card is pre-authorised (not charged).
        </li>
        <li>
          Within 7 days after the session, the Professional User must confirm completion via email
          or the payment platform.
        </li>
        <li>
          Upon confirmation, the Consumer's card is charged, and funds are transferred to the
          Professional User's bank account within 5 business days.
        </li>
      </ol>
      <p>
        If the Professional User does not confirm payment within 7 days, the Company reserves the
        right to return the payment to the Consumer. However, if the Professional User provides
        evidence that the session was completed, the Company may, at its discretion, process the
        payment to the Professional User accordingly.
      </p>
      <h3>6.3 Refunds and Withholding of Payment</h3>
      <p>
        The Company may refund the Consumer if it determines, based on documented evidence and after
        providing the Professional User with notice and an opportunity to respond, that the service
        was not fulfilled or completed as intended. In such cases, even if the Professional User has
        already received funds, the Company may request the return of such funds for the purpose of
        refunding the Consumer, provided that the process is conducted in accordance with applicable
        law and with proper justification and documentation.
      </p>
      <p>
        The Company shall provide the affected Consumer with a concise explanation of the reasons
        for the refund decision upon request, unless prohibited by law or necessary for fraud
        prevention.
      </p>
      <h3>6.4 Vouchers and Promotional Payments</h3>
      <p>
        When a Consumer uses a voucher or promotional code provided by the Company, the Professional
        User will be paid directly by the Company (not by the Consumer). In such cases:
      </p>
      <ul>
        <li>
          The Professional User must issue an invoice or proof of service (not a receipt) to the
          Company within 7 days of the session.
        </li>
        <li>
          If the document is not issued within this timeframe, the Company reserves the right to
          refuse payment.
        </li>
      </ul>
      <h3>6.5 Technical Failures</h3>
      <p>
        If a technical problem prevents a Professional User or Consumer from participating in a
        scheduled session (e.g., platform malfunction, connection failure), the Company may cancel
        the session at its discretion. In such cases:
      </p>
      <ul>
        <li>No charge will occur</li>
        <li>The Professional User is not entitled to payment</li>
        <li>The session may be rescheduled free of charge</li>
      </ul>
      <p>This remains at the sole discretion of the Company.</p>
      <h3>6.6 Automated Proof of Service</h3>
      <p>
        The Company's automated system issues proof of service provision for internal use. This
        automated proof does not replace the tax document (invoice or receipt) that the Professional
        User is required to issue under applicable tax and accounting laws.
      </p>

      <h2>7. RIGHT OF WITHDRAWAL (CONSUMER RIGHTS)</h2>
      <p>
        Consumers have the right to withdraw from a scheduled appointment within 14 calendar days
        from the date of booking, unless:
      </p>
      <ul>
        <li>The service has already been performed; or</li>
        <li>
          The Consumer expressly consents to commencement of service during the withdrawal period
          and acknowledges loss of the right of withdrawal.
        </li>
      </ul>
      <p>
        For downloadable digital content (e.g., articles, eBooks, videos): By downloading, the
        Consumer expressly waives the right of withdrawal.
      </p>
      <p>Cancellation policy for appointments:</p>
      <ul>
        <li>More than 24 hours before appointment: Full refund</li>
        <li>Less than 60 minutes before appointment: Cancellation fees apply; no refund</li>
      </ul>
      <p>
        Refunds may take up to 14 business days to be returned to the Consumer's account due to
        processing requirements.
      </p>

      <h2>8. PROFESSIONAL INDEPENDENCE</h2>
      <h3>8.1 Independent contractors</h3>
      <p>
        Professional Users are independent contractors, not employees, agents, or partners of the
        Company.
      </p>
      <h3>8.2 No exclusivity</h3>
      <p>
        This Agreement does not create an exclusive relationship. Professional Users may provide
        services to third parties and engage in other business activities.
      </p>
      <h3>8.3 Professional responsibility</h3>
      <p>Professional Users are solely responsible for:</p>
      <ul>
        <li>The content, quality, and delivery of their services</li>
        <li>Their own tax obligations, social insurance, and professional insurance</li>
        <li>Obtaining and maintaining all necessary licenses, permits, and certifications</li>
      </ul>
      <p>
        Professional Users have no authority to enter into contracts, make commitments, or act on
        behalf of the Company.
      </p>
      <p>Content Collaborations</p>
      <p>
        From time to time, the Company may invite Professional Users to participate in content
        initiatives, such as webinars, masterclasses, group sessions, live streams (including on
        Instagram and other social media platforms), interviews, podcasts, or the creation of
        educational materials. Participation in such initiatives is voluntary, but once a
        Professional User agrees to participate, any resulting Co-Created Content shall be governed
        by Section 10.3 (Co-Created Content and Commissioned Materials). The Professional User shall
        not request removal, withdrawal, or limitation of the Co-Created Content, except where
        required by mandatory law.
      </p>

      <h2>9. PROHIBITED CONDUCT</h2>
      <p>Professional Users must not:</p>
      <ul>
        <li>Create fake accounts or engage in fraudulent activity</li>
        <li>Collect, store, or misuse personal data of Consumers</li>
        <li>Engage in misleading advertising or unfair competition</li>
        <li>Use offensive, threatening, or discriminatory language</li>
        <li>
          Harass Consumers or other users (harassment includes verbal or written expressions
          intended to cause mental or physical harm)
        </li>
        <li>Upload malware, viruses, or harmful software</li>
        <li>Copy, distribute, or reproduce Platform content without written permission</li>
        <li>Use the Company's trademarks, branding, or identity without consent</li>
      </ul>
      <p>
        The main values governing the Platform are appreciation and respect. Discrimination based on
        gender, gender identity, nationality, religion, disability, sexual orientation, or age is
        strictly prohibited.
      </p>
      <p>If these rules are not followed, the Company may:</p>
      <ul>
        <li>Immediately disable the Professional User's account</li>
        <li>Terminate the Agreement</li>
        <li>Contact law enforcement authorities</li>
        <li>Seek damages and other legal remedies</li>
      </ul>

      <h2>10. INTELLECTUAL PROPERTY RIGHTS</h2>
      <h3>10.1 Company’s Rights</h3>
      <p>
        All content, design, trademarks, logos, and software on the Platform are the exclusive
        property of the Company and are protected by copyright, trademark, and other intellectual
        property rights.
      </p>
      <p>Professional Users may not:</p>
      <ul>
        <li>
          Copy, reproduce, distribute, or display Platform content without prior written consent
        </li>
        <li>
          Use the Company’s trademark or branding to identify or promote third-party products or
          services
        </li>
        <li>Reverse-engineer, disassemble, or interfere with Platform software or systems</li>
      </ul>
      <p>
        Trademark protection: "VivianLab" is a registered trademark protected under Article 6a of
        the Paris Convention and Directive (EU) 2015/2436 of the European Parliament and of the
        Council of 16 December 2015 to approximate the laws of the Member States relating to trade
        marks.
      </p>
      <h3>10.2 Professional User Profile Content</h3>
      <p>
        By uploading content to your profile (including photos, descriptions, articles, videos),
        you:
      </p>
      <ul>
        <li>Retain ownership of your content</li>
        <li>
          Grant the Company a non-exclusive, worldwide, royalty-free licence to use, display, and
          distribute your content on the Platform for the purpose of providing the Services
        </li>
        <li>
          Warrant that you have all necessary rights to such content and that it does not infringe
          third-party rights
        </li>
      </ul>
      <h3>10.3 Co-Created Content and Commissioned Materials</h3>
      <ol>
        <li>
          <strong>Assignment of Intellectual Property Rights</strong>
          <br />
          To the extent permitted by law, the Professional User hereby irrevocably assigns,
          transfers, and conveys to the Company, on an exclusive basis, all worldwide intellectual
          property rights, including but not limited to copyright and related rights, in and to any
          Co-Created Content produced in collaboration with the Company (including, without
          limitation, webinars, video recordings, online courses, interviews, articles, blog posts,
          social media content, marketing materials, and any audio-visual recordings featuring the
          Professional User), for the entire duration of protection of such rights and for all
          countries worldwide.
        </li>
        <li>
          <strong>Licence where assignment is not possible</strong>
          <br />
          Where an assignment is not legally possible, the Professional User grants the Company an
          exclusive, worldwide, perpetual, irrevocable, transferable, and sublicensable licence to
          use, reproduce, modify, adapt, translate, distribute, publish, publicly perform, publicly
          display, communicate to the public, and otherwise exploit the Co-Created Content in any
          format and through any media, whether now known or hereafter developed, without any
          additional compensation.
        </li>
        <li>
          <strong>Right to edit, translate, and adapt</strong>
          <br />
          The Company shall have the right to edit, shorten, adapt, combine with other content,
          create derivative works from, and translate the Co-Created Content into any language, and
          to use it for any commercial, educational, or marketing purpose, including but not limited
          to global distribution on the Platform, social media, partner channels, and advertising
          campaigns.
        </li>
        <li>
          <strong>Use of name, image, and likeness</strong>
          <br />
          The Professional User grants the Company the right to use their name, professional title,
          image, voice, and likeness as incorporated in the Co-Created Content, solely for the
          purposes of promoting and exploiting such content in accordance with this clause.
        </li>
        <li>
          <strong>No additional compensation</strong>
          <br />
          Unless explicitly agreed otherwise in writing, the Professional User acknowledges that any
          agreed fee or commercial arrangement for the creation of Co-Created Content fully covers
          their compensation, and they shall not be entitled to any further payment, royalty, or
          participation in revenues arising from the exploitation of the Co-Created Content by the
          Company.
        </li>
        <li>
          <strong>Moral rights</strong>
          <br />
          To the extent permitted by applicable law, the Professional User waives any moral rights
          (or agrees not to enforce them) in relation to the Co-Created Content, including the
          rights of attribution and integrity, as against the Company and its licensees, successors,
          and assignees.
        </li>
      </ol>

      <h2>11. LIMITATION OF LIABILITY</h2>
      <h3>11.1 Company’s Limitation</h3>
      <p>
        The Platform is provided for informational and intermediary purposes only. It does not
        provide medical, health, or professional advice or services.
      </p>
      <p>To the fullest extent permitted by law:</p>
      <p>The Company is not liable for:</p>
      <ul>
        <li>The content, quality, or outcome of services provided by Professional Users</li>
        <li>Cancellations, no-shows, or unfulfilled appointments</li>
        <li>
          Any damages, losses, or injuries arising from Professional User services or interactions
          with Consumers
        </li>
        <li>Loss of revenue, profits, contracts, or special, indirect, or consequential damages</li>
      </ul>
      <p>
        The Platform is provided "as is" without warranties of any kind (express or implied),
        including but not limited to warranties of merchantability, fitness for a particular
        purpose, or uninterrupted operation.
      </p>
      <p>
        However, this limitation of liability does not exclude or limit the Company’s liability for:
      </p>
      <ul>
        <li>
          Death or personal injury resulting from the Company’s own negligence or wilful misconduct
        </li>
        <li>Any mandatory statutory liability that cannot be excluded by law</li>
      </ul>
      <p>
        For the avoidance of doubt, the Company is not responsible for, and shall have no liability
        arising out of, any advice, recommendations, statements, or omissions made by Professional
        Users during individual sessions, group webinars, hotlines, social media appearances, or in
        any Professional Content or Co-Created Content.
      </p>
      <h3>11.2 Professional User Indemnification</h3>
      <p>
        Professional Users agree to indemnify, defend, and hold harmless the Company from and
        against any and all:
      </p>
      <ul>
        <li>Claims, liabilities, damages, losses, and judgments</li>
        <li>Legal fees and costs</li>
      </ul>
      <p>Arising out of or related to:</p>
      <ul>
        <li>The provision of services by the Professional User</li>
        <li>Breach of these Terms of Use</li>
        <li>Violation of applicable laws or third-party rights</li>
        <li>Negligence or wilful misconduct</li>
        <li>
          Any claim by a Consumer or third party relating to advice, information, or content
          provided by the Professional User, including Professional Content and Co-Created Content.
        </li>
      </ul>

      <h2>12. FORCE MAJEURE</h2>
      <p>
        The Company cannot be held liable for failure to perform obligations if such failure results
        from events beyond its reasonable control, including but not limited to:
      </p>
      <ul>
        <li>Acts of God, natural disasters</li>
        <li>War, terrorism, civil unrest</li>
        <li>Labor strikes or shortages</li>
        <li>Disruption of communication networks</li>
        <li>Unavailability of payment processors</li>
        <li>Government actions or legal restrictions</li>
      </ul>
      <p>
        If a force majeure event occurs, the Company will notify affected Professional Users within
        15 days and provide an estimate of the expected duration.
      </p>

      <h2>13. TERMINATION AND SUSPENSION</h2>
      <h3>13.1 Termination by the Company</h3>
      <p>
        The Company may immediately suspend or terminate a Professional User’s access to the
        Platform, without prior notice, if:
      </p>
      <ul>
        <li>The Professional User violates these Terms of Use</li>
        <li>The Professional User engages in illegal, fraudulent, or harmful activity</li>
        <li>The Professional User cancels four (4) or more appointments without valid reason</li>
        <li>
          The Company receives a court order, government directive, or regulatory notice requiring
          suspension or termination
        </li>
        <li>
          Continued provision of services would expose the Company to legal or reputational risk
        </li>
      </ul>
      <p>Written notification will be provided after such action is taken.</p>
      <h3>13.2 Termination by Professional User</h3>
      <p>Professional Users may terminate this Agreement at any time by:</p>
      <ul>
        <li>Closing their account via the Platform settings; or</li>
        <li>Sending written notice to the Company at</li>
      </ul>
      <p>
        Upon termination, the Company will delete or return personal data in accordance with the{' '}
        <ExternalLink href="/data-processing-addendum">
          Data Processing Agreement (Appendix 1)
        </ExternalLink>{' '}
        and applicable law.
      </p>
      <h3>13.3 Effects of Termination</h3>
      <p>Upon termination:</p>
      <ul>
        <li>The Professional User will no longer have access to the Platform</li>
        <li>All outstanding payments will be settled within a reasonable timeframe</li>
        <li>
          Data will be handled in accordance with the{' '}
          <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink> and{' '}
          <ExternalLink href="/data-processing-addendum">Data Processing Agreement</ExternalLink>
        </li>
        <li>
          Any provisions intended to survive termination (including confidentiality,
          indemnification, and liability) will remain in effect
        </li>
      </ul>

      <h2>14. MODIFICATION OF TERMS</h2>
      <p>
        The Company may modify these Terms of Use, the{' '}
        <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>, and related policies at
        any time for various legal reasons, including security, intellectual property protection,
        regulatory compliance, or business reasons.
      </p>
      <p>
        If a modification substantially and negatively impacts a Professional User’s access to or
        use of the Platform, the Company will notify the Professional User in advance. The
        Professional User shall then have the right to terminate the Agreement within 30 days of the
        modification.
      </p>
      <p>
        Continued use of the Platform after modifications take effect constitutes acceptance of the
        modified terms.
      </p>
      <p>
        Professional Users may save or retain a copy of these Terms at the time of registration.
      </p>

      <h2>15. ASSIGNMENT OF RIGHTS</h2>
      <ul>
        <li>
          Professional Users may not assign any rights or obligations under this Agreement without
          the Company's prior written consent.
        </li>
        <li>
          The Company may assign its rights and obligations under this Agreement to any third party
          at its discretion (e.g., in connection with a merger, acquisition, or sale of business).
        </li>
      </ul>

      <h2>16. LAW AND JURISDICTION</h2>
      <h3>16.1 Governing Law</h3>
      <p>
        This Agreement is governed by Greek law, even when the Platform is accessed from outside
        Greece.
      </p>
      <h3>16.2 Dispute Resolution</h3>
      <p>
        Any dispute arising out of or related to this Agreement shall first be subject to mediation
        in accordance with the Mediation Regulation of the Greek Ministry of Justice.
      </p>
      <p>
        If the dispute cannot be resolved through mediation, it shall be resolved exclusively by
        arbitration conducted in accordance with Arbitration Rules, or by the courts of Athens,
        which shall have exclusive jurisdiction.
      </p>
      <h3>16.3 Severability</h3>
      <p>
        If any provision of this Agreement is deemed invalid or unenforceable by a court, the
        remaining provisions shall remain in full force and effect.
      </p>

      <h2>17. ENTIRE AGREEMENT</h2>
      <p>
        These Terms of Use, together with the{' '}
        <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink> and{' '}
        <ExternalLink href="/data-processing-addendum">
          Data Processing Agreement (Appendix 1)
        </ExternalLink>
        , constitute the entire agreement between the Professional User and the Company and
        supersede all prior agreements, understandings, or communications (whether written or oral)
        relating to the subject matter.
      </p>
      <p>
        No other terms, conditions, or representations shall be binding unless agreed to in writing
        by the Company.
      </p>

      <h2>18. CONTACT INFORMATION</h2>
      <p>For questions or concerns regarding these Terms of Use, please contact:</p>
      <p>
        Company: VIVIANLAB LTD
        <br />
        Address: 1612 SOFIA BUL. TSAR BORIS III № 81 ET.1, AP.1
        <br />
        Email: INFO@VIVIANLAB.COM
        <br />
        VAT: BG207933919
      </p>

      <h2>19. IMPORTANT NOTICE: RESPECT AND APPRECIATION</h2>
      <p>
        The Platform is governed by the values of respect and appreciation. All Professional Users
        must observe these values in all interactions.
      </p>
      <p>Zero tolerance for:</p>
      <ul>
        <li>Offensive, provocative, or threatening language</li>
        <li>
          Discrimination based on gender, gender identity, nationality, religion, disability, sexual
          orientation, or age
        </li>
        <li>Harassment of any kind</li>
      </ul>
      <p>Violations may result in account deactivation and/or legal action.</p>

      <p>THESE TERMS OF USE FOR PROFESSIONAL USERS WERE LAST AMENDED ON 5/12/2025</p>
      <p>
        By clicking “I Accept” or by using the Platform, you acknowledge that you have read,
        understood, and agree to be bound by these Terms of Use, the{' '}
        <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>, and the{' '}
        <ExternalLink href="/data-processing-addendum">
          Data Processing Agreement (Appendix 1)
        </ExternalLink>
        .
      </p>
    </div>
  );
}

export default ProfessionalTosEnglish;
